// client/src/admin/pages/EmailTemplates.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  TextField,
  Button,
  Divider,
  Grid,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
  Paper,
  Tooltip,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import EditIcon from "@mui/icons-material/Edit";
import PreviewIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import CodeIcon from "@mui/icons-material/Code";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";

const TEMPLATE_META = {
  welcome: {
    label: "Welcome Email",
    color: "#6D28D9",
    description: "Sent when a new user registers",
    icon: "👋",
  },
  plan_activated: {
    label: "Plan Activated",
    color: "#059669",
    description: "Sent when a user activates or purchases a plan",
    icon: "✅",
  },
  verify_email: {
    label: "Email Verification",
    color: "#2563EB",
    description: "Sent to verify user's email address",
    icon: "📧",
  },
  password_reset: {
    label: "Password Reset",
    color: "#DC2626",
    description: "Sent when a user requests a password reset",
    icon: "🔐",
  },
};

const EmailTemplates = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();

  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [editForm, setEditForm] = useState({ subject: "", body: "" });
  const [saving, setSaving] = useState(false);
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    html: "",
    subject: "",
  });
  const [testDialog, setTestDialog] = useState({ open: false, key: "" });
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0=editor, 1=html-source

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    const res = await hitAxios({
      path: "/api/email-templates/get-all",
      admin: true,
      showSnackbar: false,
    });
    if (res?.data?.success) setTemplates(res.data.data);
  };

  const openEditor = (tpl) => {
    setSelectedKey(tpl.template_key);
    setEditForm({ subject: tpl.subject, body: tpl.body });
    setActiveTab(0);
  };

  const saveTemplate = async () => {
    setSaving(true);
    const res = await hitAxios({
      path: "/api/email-templates/update",
      post: true,
      admin: true,
      obj: {
        template_key: selectedKey,
        subject: editForm.subject,
        body: editForm.body,
      },
    });
    setSaving(false);
    if (res?.data?.success) {
      loadTemplates();
    }
  };

  const openPreview = async (key) => {
    const res = await hitAxios({
      path: "/api/email-templates/preview",
      post: true,
      admin: true,
      obj: { template_key: key },
      showSnackbar: false,
    });
    if (res?.data?.success) {
      setPreviewDialog({
        open: true,
        html: res.data.data.html,
        subject: res.data.data.subject,
      });
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) return;
    setTesting(true);
    setTestResult(null);
    const res = await hitAxios({
      path: "/api/email-templates/send-test",
      post: true,
      admin: true,
      obj: { template_key: testDialog.key, to: testEmail },
      showSnackbar: false,
    });
    setTesting(false);
    setTestResult({ success: res?.data?.success, msg: res?.data?.msg });
  };

  const selectedTemplate = templates.find(
    (t) => t.template_key === selectedKey,
  );
  const meta = selectedKey ? TEMPLATE_META[selectedKey] : null;

  // ── Template List View ──────────────────────────────────────────────────
  if (!selectedKey) {
    return (
      <Box>
        <PageHeader
          title="Email Templates"
          subtitle="Customize the emails sent to your users"
          icon={EmailIcon}
        />

        <Grid container spacing={3}>
          {templates.map((tpl) => {
            const m = TEMPLATE_META[tpl.template_key] || {};
            return (
              <Grid size={{ xs: 12, sm: 6 }} key={tpl.template_key}>
                <Card
                  sx={{
                    border: `1px solid ${alpha(m.color || "#6366F1", 0.2)}`,
                    borderRadius: 3,
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: `0 8px 24px ${alpha(m.color || "#6366F1", 0.15)}`,
                    },
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: `linear-gradient(90deg, ${m.color || "#6366F1"}, ${alpha(m.color || "#6366F1", 0.4)})`,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={2}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            bgcolor: alpha(m.color || "#6366F1", 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.4rem",
                          }}
                        >
                          {m.icon}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {m.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {m.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>

                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.background.default, 0.6),
                        border: `1px solid ${theme.palette.divider}`,
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        fontWeight={700}
                        display="block"
                        mb={0.5}
                      >
                        SUBJECT
                      </Typography>
                      <Typography variant="body2" noWrap>
                        {tpl.subject}
                      </Typography>
                    </Box>

                    {tpl.variables && (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                        mb={2}
                      >
                        {tpl.variables.split(",").map((v) => (
                          <Chip
                            key={v}
                            label={`{{${v.trim()}}}`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.6rem",
                              fontWeight: 600,
                              bgcolor: alpha(m.color || "#6366F1", 0.08),
                              color: m.color || "#6366F1",
                            }}
                          />
                        ))}
                      </Stack>
                    )}

                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => openEditor(tpl)}
                        sx={{
                          borderRadius: 2,
                          flex: 1,
                          borderColor: alpha(m.color || "#6366F1", 0.4),
                          color: m.color || "#6366F1",
                        }}
                      >
                        Edit
                      </Button>
                      <Tooltip title="Preview with sample data">
                        <IconButton
                          size="small"
                          onClick={() => openPreview(tpl.template_key)}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1.5,
                          }}
                        >
                          <PreviewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send test email">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTestDialog({
                              open: true,
                              key: tpl.template_key,
                            });
                            setTestResult(null);
                            setTestEmail("");
                          }}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1.5,
                          }}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Preview Dialog */}
        <Dialog
          open={previewDialog.open}
          onClose={() =>
            setPreviewDialog({ open: false, html: "", subject: "" })
          }
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography fontWeight={700}>Email Preview</Typography>
                <Typography variant="caption" color="text.secondary">
                  Subject: {previewDialog.subject}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() =>
                  setPreviewDialog({ open: false, html: "", subject: "" })
                }
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                m: 2,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <iframe
                srcDoc={previewDialog.html}
                style={{ width: "100%", height: 500, border: "none" }}
                title="Email Preview"
              />
            </Box>
          </DialogContent>
        </Dialog>

        {/* Test Email Dialog */}
        <Dialog
          open={testDialog.open}
          onClose={() => setTestDialog({ open: false, key: "" })}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle fontWeight={700}>Send Test Email</DialogTitle>
          <DialogContent>
            <Stack spacing={2} mt={1}>
              <Typography variant="body2" color="text.secondary">
                Send a test version of the{" "}
                <strong>{TEMPLATE_META[testDialog.key]?.label}</strong> template
                with sample data.
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Recipient Email"
                placeholder="you@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendTestEmail()}
              />
              {testResult && (
                <Alert
                  severity={testResult.success ? "success" : "error"}
                  sx={{ borderRadius: 2 }}
                >
                  {testResult.msg}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button
              onClick={() => setTestDialog({ open: false, key: "" })}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={
                testing ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              onClick={sendTestEmail}
              disabled={testing || !testEmail}
              sx={{ borderRadius: 2 }}
            >
              {testing ? "Sending..." : "Send"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ── Editor View ─────────────────────────────────────────────────────────
  return (
    <Box>
      <PageHeader
        title={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <IconButton size="small" onClick={() => setSelectedKey(null)}>
              <Box component="span" sx={{ fontSize: 18 }}>
                ←
              </Box>
            </IconButton>
            <Typography variant="h5" fontWeight={700}>
              {meta?.label}
            </Typography>
            <Chip label={meta?.icon} size="small" />
          </Stack>
        }
        subtitle={meta?.description}
        icon={EmailIcon}
        primaryAction={
          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={saveTemplate}
            disabled={saving}
            sx={{ borderRadius: 2 }}
          >
            {saving ? "Saving..." : "Save Template"}
          </Button>
        }
        secondaryActions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => openPreview(selectedKey)}
              sx={{ borderRadius: 2 }}
            >
              Preview
            </Button>
            <Button
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={() => {
                setTestDialog({ open: true, key: selectedKey });
                setTestResult(null);
                setTestEmail("");
              }}
              sx={{ borderRadius: 2 }}
            >
              Test
            </Button>
          </Stack>
        }
      />

      <Grid container spacing={3}>
        {/* Left: Editor */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Email Subject"
                  value={editForm.subject}
                  onChange={(e) =>
                    setEditForm({ ...editForm, subject: e.target.value })
                  }
                  helperText="You can use {{variables}} in the subject"
                />

                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1.5}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      Email Body (HTML)
                    </Typography>
                    <Tabs
                      value={activeTab}
                      onChange={(_, v) => setActiveTab(v)}
                      sx={{ minHeight: 32 }}
                    >
                      <Tab
                        label="Editor"
                        sx={{ minHeight: 32, py: 0.5, fontSize: "0.75rem" }}
                      />
                      <Tab
                        label="HTML Source"
                        icon={<CodeIcon sx={{ fontSize: 14 }} />}
                        iconPosition="start"
                        sx={{ minHeight: 32, py: 0.5, fontSize: "0.75rem" }}
                      />
                    </Tabs>
                  </Stack>

                  {activeTab === 0 && (
                    <TextField
                      fullWidth
                      multiline
                      rows={20}
                      value={editForm.body}
                      onChange={(e) =>
                        setEditForm({ ...editForm, body: e.target.value })
                      }
                      sx={{
                        "& textarea": {
                          fontFamily: "monospace",
                          fontSize: "0.8rem",
                        },
                      }}
                    />
                  )}

                  {activeTab === 1 && (
                    <Box
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <iframe
                        srcDoc={editForm.body}
                        style={{ width: "100%", height: 480, border: "none" }}
                        title="HTML Preview"
                      />
                    </Box>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Variables + Tips */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={3}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} mb={2}>
                  Available Variables
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={2}
                >
                  Click to copy. Use these in your subject and body.
                </Typography>
                <Stack spacing={1}>
                  {selectedTemplate?.variables?.split(",").map((v) => {
                    const trimmed = v.trim();
                    return (
                      <Paper
                        key={trimmed}
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          borderRadius: 1.5,
                          cursor: "pointer",
                          "&:hover": {
                            bgcolor: alpha(meta?.color || "#6366F1", 0.05),
                            borderColor: meta?.color || "#6366F1",
                          },
                          transition: "all 0.15s",
                        }}
                        onClick={() =>
                          navigator.clipboard.writeText(`{{${trimmed}}}`)
                        }
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            variant="caption"
                            fontFamily="monospace"
                            fontWeight={700}
                            color={meta?.color || "primary.main"}
                          >
                            {`{{${trimmed}}}`}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ fontSize: "0.6rem" }}
                          >
                            click to copy
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                  💡 Tips
                </Typography>
                <Stack spacing={1}>
                  {[
                    "Use {{variable}} syntax to insert dynamic values",
                    "Test your template before going live",
                    "Keep subject lines under 60 characters",
                    "Inline CSS works best for email clients",
                    "Always include a plain-text fallback in your HTML",
                  ].map((tip, i) => (
                    <Typography
                      key={i}
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "flex", gap: 0.75 }}
                    >
                      <span
                        style={{
                          color: meta?.color || "#6366F1",
                          flexShrink: 0,
                        }}
                      >
                        •
                      </span>
                      {tip}
                    </Typography>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Test Email Dialog */}
      <Dialog
        open={testDialog.open}
        onClose={() => setTestDialog({ open: false, key: "" })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={700}>Send Test Email</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              fullWidth
              size="small"
              label="Recipient Email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
            {testResult && (
              <Alert
                severity={testResult.success ? "success" : "error"}
                sx={{ borderRadius: 2 }}
              >
                {testResult.msg}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setTestDialog({ open: false, key: "" })}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={
              testing ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SendIcon />
              )
            }
            onClick={sendTestEmail}
            disabled={testing || !testEmail}
            sx={{ borderRadius: 2 }}
          >
            {testing ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailTemplates;
